import { Columns } from 'components/shared/Columns';
import { ContentBox } from 'components/shared/ContentBox';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const capitalization = [
    <Columns
        key="1"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h3>Sentence Case</h3>
                    <p>
                        Only the first letter of the first word of the sentence, proper nouns, and
                        specific words that require capitalization (like acronyms) are capitalized.
                        The rest of the words are in lowercase.
                    </p>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 237 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/dropdown-capitalization.svg"
                            alt="Dropdown menu with sentence case examples"
                            loading="lazy"
                        />
                    }
                />
            </>
        }
    />,
];

const listOrder = [
    <p key="2">
        Selecting the appropriate method depends on the nature of the options and the context in
        which the dropdown is used.
    </p>,
    <Columns
        key="3"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h3>Priority</h3>
                    <h4>Where we use this list order</h4>
                    <ul>
                        <li>Navigation</li>
                        <li>Actions</li>
                    </ul>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 212 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/dropdown-list-order-priority.svg"
                            alt="Dropdown menu options in priority order"
                            loading="lazy"
                        />
                    }
                />
                <div>
                    <h3>Grouped</h3>
                    <p>
                        Arranging options by category or functionality. Grouped options should be in
                        alphabetical order and can used in combination with other list orders.
                    </p>
                    <h4>Where we use this list order</h4>
                    <ul>
                        <li>Profile</li>
                        <li>Navigation</li>
                    </ul>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 173 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/dropdown-list-order-grouped.svg"
                            alt="Dropdown menu options grouped together"
                            loading="lazy"
                        />
                    }
                />
                <div>
                    <h3>Alphabetical</h3>
                    <p>
                        If neither grouped order nor priority order is appropriate for a list, use
                        alphabetical order.
                    </p>
                    <h4>Where we use this list order</h4>
                    <ul>
                        <li>Reason field</li>
                        <li>Issue field</li>
                    </ul>
                </div>
                <ContentBox
                    centerContent
                    content={
                        <img
                            style={{ width: 208 }}
                            src="https://sseasacdn.blob.core.windows.net/strata-website/dropdown-list-order-alphabetical.svg"
                            alt="Dropdown menu options in alphabetical order"
                            loading="lazy"
                        />
                    }
                />
            </>
        }
    />,
];

const listOrderExceptions = [
    <div key="4">
        <h3>&quot;Other&quot; Option</h3>
        <p>
            In dropdown menus, the option &quot;Other&quot; is an exception to the standard ordering
            rules and is always placed at the end of the list. This option is commonly used in
            drop-down menus where users are asked to select from a predefined list of options. When
            a user selects &quot;Other,&quot; it triggers an additional input field titled
            &quot;Other Reason,&quot; where they can manually enter their own reason.
        </p>
    </div>,
    <div key="5">
        <h3>Other Orders</h3>
        <ul>
            <li>Chronological order</li>
            <li>Geographic/spatial order</li>
            <li>
                Contextual relevance: Options adjust based on of previous selections or user&#39;s
                current context.
            </li>
            <li>Hierarchical order: Order is based on of the relationship between options.</li>
        </ul>
    </div>,
];

const variants = [
    <p key="6">
        Options may be displayed as disabled or with secondary text. Refer to the guidelines below
        to determine when to apply these.
    </p>,
    <div key="7">
        <h3>Disabled Options</h3>
        <p>
            Disabling options instead of hiding them can be less confusing for users and let them
            know about all the possible options that can be selected and the conditions under which
            they can be selected.
        </p>
        <h4>When we use disabled options</h4>
        <ul>
            <li>When an option has already been selected</li>
            <li>When an option cannot be selected</li>
        </ul>
    </div>,
    <ContentBox
        key="8"
        centerContent
        content={
            <img
                style={{ width: 366 }}
                src="https://sseasacdn.blob.core.windows.net/strata-website/dropdown-variants-disabled-options.svg"
                alt="Dropdown menu options with disabled options"
                loading="lazy"
            />
        }
    />,
    <div key="9">
        <h3>Secondary Text</h3>
        <p>
            Adding secondary text to options in a dropdown can help users better understand their
            choices so they can make more informed decisions.
        </p>
        <h4>Use in the following scenarios</h4>
        <ul>
            <li>
                When certain options aren&#39;t applicable based on previous selections or context
            </li>
            <li>To explain options that may be ambiguous or not self-explanatory</li>
            <li>
                To provide the user with additional information about an option such as ID codes
            </li>
            <li>To categorize options</li>
        </ul>
    </div>,
    <ContentBox
        key="10"
        centerContent
        content={
            <img
                style={{ width: 426 }}
                src="https://sseasacdn.blob.core.windows.net/strata-website/dropdown-variants-secondary-text.svg"
                alt="Dropdown menu options with secondary text"
                loading="lazy"
            />
        }
    />,
];

export const DropdownOptions = () => (
    <div>
        <Page
            pageTitle="Dropdown Options"
            pageDescription={
                <p>
                    Use these guidelines as a source of truth for best practices and patterns when
                    creating dropdowns.
                </p>
            }
            pageContent={
                <>
                    <Section title="Capitalization" contentArray={capitalization} />
                    <Section title="List Order" contentArray={listOrder} />
                    <Section title="List Order Exceptions" contentArray={listOrderExceptions} />
                    <Section title="Variants" contentArray={variants} />
                </>
            }
        />
    </div>
);
